// Where a mail written from the mailbox gets filed, as pure functions of what
// the composer knows. Extracted from the components on purpose: this is the
// whole correctness argument for which timeline an email lands on, it has one
// subtle rule per input, and inline in a component it can only be checked by
// reading. `messageFiling.ts` next door is split the same way.

// Where the contact lookup for the CURRENT addresses stands (AttachToPicker).
// `loading`: no answer yet, the agent has had no chance to decide. `ready`:
// the matches describe the addresses on screen. `unavailable`: the lookup
// failed, so Apollo's `previousData` — the matches of the PREVIOUS address
// list — is all that is left, and it cannot confirm anything about these
// recipients.
export type PickerState = 'loading' | 'ready' | 'unavailable';

export const pickerState = ({
  loading,
  hasData,
  hasError,
}: {
  loading: boolean;
  hasData: boolean;
  hasError: boolean;
}): PickerState => {
  if (loading) {
    return 'loading';
  }
  if (hasError) {
    return 'unavailable';
  }
  // Neither settled nor failed: a skipped query. Send-blocking is the
  // conservative end, and the picker is not mounted without addresses anyway.
  return hasData ? 'ready' : 'loading';
};

// Send waits while the lookup is in flight, but not on a failure: a query that
// never resolves must not strand a written mail behind a disabled button.
export const filingSettled = (state: PickerState): boolean =>
  state !== 'loading';

// The parents the request carries. A choice the picker could not confirm
// against the current recipients is held back — the mail is filed nowhere,
// which the agent can redo, rather than on a contact who may have left the To
// field. It is held back, not cleared: the caller keeps it in state and in the
// session draft, so a lookup that recovers brings it back.
export const sentParentIds = ({
  hostParentIds,
  showAttachTo,
  state,
  attachParentIds,
}: {
  hostParentIds: string[];
  showAttachTo: boolean;
  state: PickerState;
  attachParentIds: string[];
}): string[] => {
  if (hostParentIds.length > 0) {
    return hostParentIds;
  }
  if (!showAttachTo) {
    return attachParentIds;
  }
  return state === 'ready' ? attachParentIds : [];
};

// Whether the composer is claiming a filing decision. False leaves the filing
// to the service, whose default matches tenant-wide; true means "this is the
// answer", an empty list included.
export const filingResolved = ({
  hostParentIds,
  showAttachTo,
  state,
}: {
  hostParentIds: string[];
  showAttachTo: boolean;
  state: PickerState;
}): boolean =>
  hostParentIds.length > 0 || (showAttachTo && filingSettled(state));

// The picker's value once the matches for the current addresses are in: a
// parent of a contact who left the recipients goes, and a single match nobody
// has answered for yet is filed on the contact. Both in one pass: a value that
// still held a departed contact's parent would otherwise be pruned and never
// preselected (To emptied, then a new contact added). Null when nothing moves.
export const reconciledParentIds = ({
  matches,
  value,
  decided,
}: {
  // Each matched contact, its own parent first, then its leads'.
  matches: { userId: string; parents: string[] }[];
  value: string[];
  decided: string[];
}): string[] | null => {
  const allowed = new Set(matches.flatMap(match => match.parents));
  const kept = value.filter(id => allowed.has(id));
  const only = matches.length === 1 ? matches[0] : null;
  const next =
    only != null && kept.length === 0 && !decided.includes(only.userId)
      ? [only.parents[0]]
      : kept;
  const unchanged =
    next.length === value.length && next.every((id, i) => id === value[i]);
  return unchanged ? null : next;
};
